import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RadioTower, RefreshCcw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, PageHeader, StatusPill } from "@/components/uiext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

function kindForStatus(code) {
  if (!code) return "muted";
  if (code >= 500) return "danger";
  if (code >= 400) return "warn";
  if (code >= 300) return "info";
  return "ok";
}

/**
 * Unclassified-SIP ingress feed. Shows every REGISTER / INVITE / OPTIONS
 * the engine rejected before attributing it to a known customer/trunk.
 * Handy for debugging: "why won't my softphone register?" usually turns up
 * here as "unknown domain 'acme.com'".
 *
 * Platform admins see global traffic; tenant owners/operators see only the
 * packets attributed to their org (when the engine could resolve an org
 * from the To-domain or trunk IP).
 */
export default function IngressPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isPlatformAdmin = user?.scope === "platform";
  const [rows, setRows] = useState([]);
  const [method, setMethod] = useState("");
  const [auto, setAuto] = useState(true);
  const [loading, setLoading] = useState(false);

  const endpoint = useMemo(
    () => (isPlatformAdmin ? "/platform/ingress-traces" : "/ingress-traces"),
    [isPlatformAdmin]
  );

  const load = async () => {
    setLoading(true);
    try {
      const qs = method ? `?method=${encodeURIComponent(method)}` : "";
      const r = await api.get(`${endpoint}${qs}`);
      setRows((r.data && r.data.traces) || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (!auto) return;
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, method, auto]);

  return (
    <div data-testid="page-ingress">
      <PageHeader
        title={t("pages.ingress.title") || "SIP Ingress"}
        subtitle={
          t("pages.ingress.subtitle") ||
          "Unclassified or rejected SIP packets arriving at the engine. Use this to diagnose why a softphone or carrier isn't getting through."
        }
        icon={RadioTower}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant={auto ? "default" : "outline"}
              size="sm"
              onClick={() => setAuto((v) => !v)}
              data-testid="ingress-auto"
            >
              {auto
                ? t("pages.ingress.autoOn") || "Auto-refresh ON"
                : t("pages.ingress.autoOff") || "Auto-refresh OFF"}
            </Button>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ingress-refresh">
              <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
              {t("common.refresh") || "Refresh"}
            </Button>
          </div>
        }
      />

      <div className="flex items-end gap-2 mb-3">
        <div className="w-56">
          <Label className="text-xs">{t("pages.ingress.filterMethod") || "Filter by method"}</Label>
          <Input
            placeholder="REGISTER / INVITE / OPTIONS"
            value={method}
            onChange={(e) => setMethod(e.target.value.toUpperCase())}
            data-testid="ingress-filter-method"
          />
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title={t("pages.ingress.emptyTitle") || "No ingress events"}
          description={
            t("pages.ingress.emptyDesc") ||
            "Either nothing has hit the engine yet, or every packet matched a known customer/trunk (ideal). Send a REGISTER or INVITE from your softphone / PBX and refresh."
          }
        />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader className="sticky-table-head">
              <TableRow>
                <TableHead className="w-[180px]">{t("common.ts") || "Timestamp"}</TableHead>
                <TableHead>{t("pages.ingress.peer") || "Peer"}</TableHead>
                <TableHead>{t("pages.ingress.method") || "Method"}</TableHead>
                <TableHead>{t("pages.ingress.status") || "Status"}</TableHead>
                <TableHead>{t("pages.ingress.reason") || "Reason"}</TableHead>
                <TableHead className="hidden lg:table-cell">
                  {t("pages.ingress.from") || "From"}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="row-hover" data-testid={`ingress-row-${r.id}`}>
                  <TableCell className="font-mono text-[11px]">
                    {new Date(r.ts).toLocaleString()}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.peer_ip || "—"}</TableCell>
                  <TableCell>
                    <StatusPill kind="info">{r.method || "—"}</StatusPill>
                  </TableCell>
                  <TableCell>
                    {r.status != null ? (
                      <StatusPill kind={kindForStatus(r.status)}>{r.status}</StatusPill>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{r.summary || "—"}</TableCell>
                  <TableCell className="hidden lg:table-cell text-[11px] font-mono truncate max-w-[260px]">
                    {r.headers_excerpt?.From || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
